import type { PunchActionType, PunchEvent, WorkState } from '../types/employee';
import {
  formatPunchTime,
  isoToTimeInputValue,
  parseTimeOnDate,
  todayDateKey,
} from './punchServiceDateUtils';
import {
  getEffectiveEventsForDate,
  getEmployeeEventsForDate,
  getVoidedEventIds,
  isEventVoided,
} from './punchServiceEvents';
import type {
  AddMissingPunch,
  CorrectionConflictReason,
  CorrectionPlan,
  DayCorrectionAudit,
  DayRangeCorrectionOp,
} from './punchServiceTypes';

function isCorrectionPunchEvent(event: PunchEvent): boolean {
  return event.id.startsWith('corr-') && Boolean(event.reason && event.approvedByEmployeeId);
}

function isVoidCorrectionEvent(event: PunchEvent): boolean {
  return event.type === 'void' && Boolean(event.reason && event.approvedByEmployeeId);
}

/** Collect office correction audit entries for one employee day (synced with office portal). */
export function collectDayCorrectionAudits(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string
): DayCorrectionAudit[] {
  const dayEvents = getEmployeeEventsForDate(events, employeeId, dateKey);
  const usedCorrIds = new Set<string>();
  const audits: DayCorrectionAudit[] = [];

  for (const event of dayEvents) {
    if (!isVoidCorrectionEvent(event)) continue;

    const target = events.find((e) => e.id === event.voidsEventId);
    const voidedType =
      target && target.type !== 'void' ? (target.type as PunchActionType) : undefined;
    const voidedTime = target ? formatPunchTime(target.at) : undefined;

    const pairedCorr = dayEvents.find(
      (e) =>
        isCorrectionPunchEvent(e) &&
        e.reason === event.reason &&
        e.approvedByEmployeeId === event.approvedByEmployeeId &&
        !usedCorrIds.has(e.id) &&
        Math.abs(new Date(e.at).getTime() - new Date(event.at).getTime()) < 120_000
    );

    if (pairedCorr) {
      usedCorrIds.add(pairedCorr.id);
      audits.push({
        id: event.id,
        correctionAt: event.at,
        action: 'replaced',
        reason: event.reason!,
        reasonNote: event.reasonNote ?? pairedCorr.reasonNote,
        approvedByEmployeeId: event.approvedByEmployeeId!,
        voidedType,
        voidedTime,
        newType: pairedCorr.type as PunchActionType,
        newTime: formatPunchTime(pairedCorr.at),
      });
    } else {
      audits.push({
        id: event.id,
        correctionAt: event.at,
        action: 'removed',
        reason: event.reason!,
        reasonNote: event.reasonNote,
        approvedByEmployeeId: event.approvedByEmployeeId!,
        voidedType,
        voidedTime,
      });
    }
  }

  for (const event of dayEvents) {
    if (!isCorrectionPunchEvent(event) || usedCorrIds.has(event.id)) continue;
    audits.push({
      id: event.id,
      correctionAt: event.at,
      action: 'added',
      reason: event.reason!,
      reasonNote: event.reasonNote,
      approvedByEmployeeId: event.approvedByEmployeeId!,
      newType: event.type as PunchActionType,
      newTime: formatPunchTime(event.at),
    });
  }

  return audits.sort(
    (a, b) => new Date(a.correctionAt).getTime() - new Date(b.correctionAt).getTime()
  );
}

export function dayHasCorrections(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string
): boolean {
  return collectDayCorrectionAudits(events, employeeId, dateKey).length > 0;
}

function getCorrectableDayEvents(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string
): PunchEvent[] {
  return getEffectiveEventsForDate(events, employeeId, dateKey).filter(
    (e) => e.type !== 'void' && !isEventVoided(events, e.id)
  );
}

export function buildDayRangeReplacePlan(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string,
  inTime: string,
  outTime: string
): DayRangeCorrectionOp[] {
  const correctable = getCorrectableDayEvents(events, employeeId, dateKey);
  const inAt = parseTimeOnDate(inTime, dateKey);
  const outAt = parseTimeOnDate(outTime, dateKey);
  const ops: DayRangeCorrectionOp[] = [];

  for (const event of correctable) {
    if (event.type === 'lunch-start' || event.type === 'lunch-end') {
      ops.push({ kind: 'void', eventId: event.id });
    }
  }

  const existingIn = correctable.find((e) => e.type === 'in');
  const existingOut = correctable.find((e) => e.type === 'out');

  if (existingIn) {
    if (isoToTimeInputValue(existingIn.at) !== inTime) {
      ops.push({ kind: 'replace', eventId: existingIn.id, type: 'in', at: inAt });
    }
  } else {
    ops.push({ kind: 'add', type: 'in', at: inAt });
  }

  if (existingOut) {
    if (isoToTimeInputValue(existingOut.at) !== outTime) {
      ops.push({ kind: 'replace', eventId: existingOut.id, type: 'out', at: outAt });
    }
  } else {
    ops.push({ kind: 'add', type: 'out', at: outAt });
  }

  return ops;
}

export function buildAddMissingFromRange(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string,
  inTime: string,
  outTime: string
): AddMissingPunch[] {
  const correctable = getCorrectableDayEvents(events, employeeId, dateKey);
  const punches: AddMissingPunch[] = [];
  if (!correctable.some((e) => e.type === 'in')) {
    punches.push({ type: 'in', at: parseTimeOnDate(inTime, dateKey) });
  }
  if (!correctable.some((e) => e.type === 'out')) {
    punches.push({ type: 'out', at: parseTimeOnDate(outTime, dateKey) });
  }
  return punches;
}

function appendSimulatedOp(
  accumulated: PunchEvent[],
  employeeId: string,
  op: DayRangeCorrectionOp
): PunchEvent[] {
  if (op.kind === 'void') {
    return [
      ...accumulated,
      {
        id: `sim-void-${op.eventId}`,
        employeeId,
        type: 'void',
        at: new Date().toISOString(),
        source: 'office',
        voidsEventId: op.eventId,
      },
    ];
  }
  if (op.kind === 'replace') {
    return [
      ...accumulated,
      {
        id: `sim-void-${op.eventId}`,
        employeeId,
        type: 'void',
        at: new Date().toISOString(),
        source: 'office',
        voidsEventId: op.eventId,
      },
      {
        id: `sim-replace-${op.type}-${op.at}`,
        employeeId,
        type: op.type,
        at: op.at,
        source: 'office',
      },
    ];
  }
  return [
    ...accumulated,
    {
      id: `sim-add-${op.type}-${op.at}`,
      employeeId,
      type: op.type,
      at: op.at,
      source: 'office',
    },
  ];
}

export function validateDayRangeOpsPlan(
  events: PunchEvent[],
  employeeId: string,
  dateKey: string,
  ops: DayRangeCorrectionOp[]
): { ok: true } | { ok: false; reason: CorrectionConflictReason } {
  if (ops.length === 0) return { ok: false, reason: 'unchanged' };

  let accumulated = events;
  for (const op of ops) {
    const plan: CorrectionPlan =
      op.kind === 'void'
        ? { mode: 'void', voidEventId: op.eventId }
        : op.kind === 'replace'
          ? { mode: 'replace', voidEventId: op.eventId, type: op.type, at: op.at }
          : { mode: 'add', type: op.type, at: op.at };

    const result = validateCorrectionPlan(accumulated, employeeId, plan, dateKey);
    if (!result.ok) return result;
    accumulated = appendSimulatedOp(accumulated, employeeId, op);
  }

  return { ok: true };
}

const ADD_MISSING_PUNCH_ORDER: PunchActionType[] = ['in', 'lunch-start', 'lunch-end', 'out'];

export function validateAddMissingPlan(
  events: PunchEvent[],
  employeeId: string,
  punches: AddMissingPunch[],
  dateKey: string
): { ok: true } | { ok: false; reason: CorrectionConflictReason } {
  if (punches.length === 0) {
    return { ok: false, reason: 'invalid-sequence' };
  }

  const sorted = [...punches].sort(
    (a, b) => ADD_MISSING_PUNCH_ORDER.indexOf(a.type) - ADD_MISSING_PUNCH_ORDER.indexOf(b.type)
  );

  let accumulated = events;
  for (const punch of sorted) {
    const result = validateCorrectionPlan(
      accumulated,
      employeeId,
      { mode: 'add', type: punch.type, at: punch.at },
      dateKey
    );
    if (!result.ok) return result;
    accumulated = [
      ...accumulated,
      {
        id: `sim-${punch.type}-${punch.at}`,
        employeeId,
        type: punch.type,
        at: punch.at,
        source: 'office',
      },
    ];
  }

  return { ok: true };
}

export function getSimulatedEffectiveEvents(
  events: PunchEvent[],
  employeeId: string,
  plan: CorrectionPlan,
  dateKey = todayDateKey()
): PunchEvent[] {
  const voided = getVoidedEventIds(events);
  if (plan.mode === 'void' || plan.mode === 'replace') {
    voided.add(plan.voidEventId);
  }

  const simulated: PunchEvent[] = getEmployeeEventsForDate(events, employeeId, dateKey)
    .filter((e) => e.type !== 'void' && !voided.has(e.id))
    .map((e) => ({ ...e }));

  if (plan.mode === 'add') {
    simulated.push({
      id: 'sim-add',
      employeeId,
      type: plan.type,
      at: plan.at,
      source: 'office',
    });
  } else if (plan.mode === 'replace') {
    simulated.push({
      id: 'sim-replace',
      employeeId,
      type: plan.type,
      at: plan.at,
      source: 'office',
    });
  }

  return simulated.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

export function validateDayPunchSequence(
  effectiveEvents: PunchEvent[]
): { ok: true } | { ok: false; reason: CorrectionConflictReason } {
  let state: WorkState = 'none';
  let lastInAt: number | null = null;
  let lastLunchStartAt: number | null = null;

  for (const event of effectiveEvents) {
    if (event.type === 'void') continue;
    const at = new Date(event.at).getTime();

    switch (event.type) {
      case 'in':
        if (state !== 'none') return { ok: false, reason: 'invalid-sequence' };
        state = 'working';
        lastInAt = at;
        break;
      case 'lunch-start':
        if (state !== 'working') return { ok: false, reason: 'invalid-sequence' };
        if (lastInAt !== null && at < lastInAt) return { ok: false, reason: 'out-of-order' };
        state = 'on_lunch';
        lastLunchStartAt = at;
        break;
      case 'lunch-end':
        if (state !== 'on_lunch') return { ok: false, reason: 'invalid-sequence' };
        if (lastLunchStartAt !== null && at <= lastLunchStartAt) {
          return { ok: false, reason: 'out-of-order' };
        }
        state = 'working';
        lastLunchStartAt = null;
        break;
      case 'out':
        if (state !== 'working') return { ok: false, reason: 'invalid-sequence' };
        if (lastInAt !== null && at < lastInAt) return { ok: false, reason: 'out-of-order' };
        state = 'done';
        break;
      default:
        break;
    }
  }

  return { ok: true };
}

export function validateCorrectionPlan(
  events: PunchEvent[],
  employeeId: string,
  plan: CorrectionPlan,
  dateKey = todayDateKey()
): { ok: true } | { ok: false; reason: CorrectionConflictReason } {
  if (plan.mode === 'add' || plan.mode === 'replace') {
    if (new Date(plan.at).getTime() > Date.now()) {
      return { ok: false, reason: 'future-time' };
    }
  }

  if (plan.mode === 'replace') {
    const target = events.find((e) => e.id === plan.voidEventId);
    if (
      target &&
      target.type === plan.type &&
      formatPunchTime(target.at) === formatPunchTime(plan.at)
    ) {
      return { ok: false, reason: 'unchanged' };
    }
  }

  const simulated = getSimulatedEffectiveEvents(events, employeeId, plan, dateKey);
  return validateDayPunchSequence(simulated);
}
